import api from "@/api";
import converters from "@/converters";
import type { MealDishFragment } from "@/graphql";
import type { MealDishInput } from "@/graphql/generated-types";
import isDataReady from "@/helpers/requests/isDataReady";
import type { ICachedData, IMultiSelectItem, IOption } from "@/interfaces";
import { useAuthStore } from "@/stores/authStore";
import { computed, ref } from "vue";

const DEFAULT_DATA: ICachedData<MealDishFragment[]> = {
  status: "none",
  data: [],
};

const dataMap = ref<ICachedData<MealDishFragment[]>>({
  ...DEFAULT_DATA,
});

export function useMealDishes(isInit: boolean = false) {
  const authStore = useAuthStore();
  const init = async () => {
    const item = dataMap.value;

    if (item.status === "loaded" || isInit) {
      return;
    }

    item.status = "loading";
    const data = await api.mealDishes.getAll();
    item.data = data || [];
    item.status = "loaded";
  };

  init();

  const mealDishes = computed<MealDishFragment[]>(() => dataMap.value.data);

  const mealDishesList = computed<IMultiSelectItem[]>(() => {
    return dataMap.value.data
      .map(converters.mealDishes.toMultiSelectItem)
      .toSorted((a: IMultiSelectItem, b: IMultiSelectItem) =>
        a.title.localeCompare(b.title, authStore.institutionLocale),
      );
  });

  const mealDishesOptions = computed<IOption[]>(() => {
    return dataMap.value.data
      .map(converters.mealDishes.toOption)
      .toSorted((a: IOption, b: IOption) =>
        a.title.localeCompare(b.title, authStore.institutionLocale),
      );
  });

  const loading = computed(() => {
    return dataMap.value.status === "loading";
  });

  const status = computed(() => dataMap.value.status);

  const isReady = () => isDataReady(status);

  const refetch = async () => {
    const data = await api.mealDishes.getAll();
    dataMap.value.data = data || [];
  };

  const save = async (input: MealDishInput) => {
    const response = await api.mealDishes.save(input);
    await refetch();
    return response;
  };

  const deleteItem = async (id: string) => {
    const response = await api.mealDishes.deleteItem(id);
    await refetch();
    return response;
  };

  return {
    mealDishes,
    mealDishesList,
    mealDishesOptions,
    loading,
    refetch,
    isReady,
    save,
    deleteItem,
  };
}
